"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { smartFetch } from '../../../foundation/smartfetch';
import { buildUrl } from '../../../foundation/urlbuilder';
/**
 * SaveLoadSection — UI for saving and loading page JSON to/from the configured API endpoint.
 *
 * @param {object} [props.pageData] - Current page data object to be saved.
 * @param {function} [props.onLoad] - Callback invoked with loaded page data when a saved page is selected.
 * @param {string} [props.apiEndpoint] - Base API endpoint used for save/load operations.
 */
SaveLoadSection.propTypes = {
    /** Current page data object to save */
    pageData: PropTypes.object.isRequired,
    /** Callback invoked when a saved page is loaded */
    onLoad: PropTypes.func.isRequired,
    /** API endpoint base for save/load interactions */
    apiEndpoint: PropTypes.string,
};
export function SaveLoadSection({ pageData, onLoad, apiEndpoint = '/api/pagebuilder' }) {
    const [pageName, setPageName] = useState('');
    const [savedPages, setSavedPages] = useState([]);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showLoadList, setShowLoadList] = useState(false);
    // Fetch list of saved pages on mount
    useEffect(() => {
        fetchPages();
    }, [apiEndpoint]);
    async function fetchPages() {
        try {
            const result = await smartFetch(`${apiEndpoint}/list`);
            if (result.success) {
                setSavedPages(result.pages);
            }
        }
        catch (error) {
            console.error('Failed to fetch pages:', error);
        }
    }
    async function handleSave() {
        if (!pageName.trim()) {
            setMessage('Please enter a page name');
            return;
        }
        setIsLoading(true);
        setMessage('');
        try {
            const result = await smartFetch(`${apiEndpoint}/save`, {
                requestInit: {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: pageName, data: pageData })
                }
            });
            if (result.success) {
                setMessage(`✓ ${result.message}`);
                fetchPages(); // Refresh list
                setTimeout(() => setMessage(''), 3000);
            }
            else {
                setMessage(`✗ ${result.message}`);
            }
        }
        catch (error) {
            setMessage(`✗ Failed to save: ${error}`);
        }
        finally {
            setIsLoading(false);
        }
    }
    async function handleLoad(name) {
        setIsLoading(true);
        setMessage('');
        try {
            const loadUrl = buildUrl({
                baseUrl: apiEndpoint || '/api/pagebuilder',
                pathSegments: ['load'],
                params: { name }
            });
            const result = await smartFetch(loadUrl);
            if (result.success && result.data) {
                onLoad(result.data);
                setPageName(name);
                setShowLoadList(false);
                setMessage(`✓ Loaded "${name}"`);
                setTimeout(() => setMessage(''), 3000);
            }
            else {
                setMessage(`✗ ${result.message}`);
            }
        }
        catch (error) {
            setMessage(`✗ Failed to load: ${error}`);
        }
        finally {
            setIsLoading(false);
        }
    }
    async function handleDelete(name) {
        if (!confirm(`Delete page "${name}"? This cannot be undone.`)) {
            return;
        }
        setIsLoading(true);
        setMessage('');
        try {
            const deleteUrl = buildUrl({
                baseUrl: apiEndpoint || '/api/pagebuilder',
                pathSegments: ['delete'],
                params: { name }
            });
            const result = await smartFetch(deleteUrl, {
                requestInit: {
                    method: 'DELETE'
                }
            });
            if (result.success) {
                setMessage(`✓ ${result.message}`);
                fetchPages(); // Refresh list
                setTimeout(() => setMessage(''), 3000);
            }
            else {
                setMessage(`✗ ${result.message}`);
            }
        }
        catch (error) {
            setMessage(`✗ Failed to delete: ${error}`);
        }
        finally {
            setIsLoading(false);
        }
    }
    return (_jsxs("div", { style: { marginBottom: '1rem', padding: '1rem', background: '#f9f9f9', borderRadius: '4px' }, children: [_jsxs("div", { style: { marginBottom: '0.5rem' }, children: [_jsx("label", { htmlFor: "page-name", style: { display: 'block', fontWeight: 'bold', marginBottom: '0.25rem' }, children: "Page Name:" }), _jsx("input", { id: "page-name", type: "text", value: pageName, onChange: (e) => setPageName(e.target.value), placeholder: "my-landing-page", disabled: isLoading, style: {
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '1rem'
                        } })] }), _jsxs("div", { style: { display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }, children: [_jsxs("button", { onClick: handleSave, disabled: isLoading || !pageName.trim(), className: "button", style: {
                            flex: 1,
                            background: '#4CAF50',
                            color: 'white',
                            opacity: isLoading || !pageName.trim() ? 0.5 : 1
                        }, children: [_jsx("span", { role: "img", "aria-label": "save", children: "\uD83D\uDCBE" }), " Save Page"] }), _jsxs("button", { onClick: () => setShowLoadList(!showLoadList), disabled: isLoading, className: "button", style: {
                            flex: 1,
                            background: '#2196F3',
                            color: 'white',
                            opacity: isLoading ? 0.5 : 1
                        }, children: [_jsx("span", { role: "img", "aria-label": "folder", children: "\uD83D\uDCC1" }), " ", showLoadList ? 'Hide' : 'Load Page'] })] }), showLoadList && (_jsx("div", { style: {
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: 'white',
                    maxHeight: '200px',
                    overflowY: 'auto'
                }, children: savedPages.length === 0 ? (_jsx("p", { style: { color: '#666', fontStyle: 'italic' }, children: "No saved pages" })) : (_jsx("ul", { style: { listStyle: 'none', padding: 0, margin: 0 }, children: savedPages.map(page => (_jsxs("li", { style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem',
                            borderBottom: '1px solid #eee'
                        }, children: [_jsx("button", { onClick: () => handleLoad(page), style: {
                                    flex: 1,
                                    textAlign: 'left',
                                    background: 'none',
                                    border: 'none',
                                    color: '#2196F3',
                                    cursor: 'pointer',
                                    padding: '0.25rem',
                                    fontSize: '1rem'
                                }, children: page }), _jsx("button", { onClick: () => handleDelete(page), style: {
                                    background: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.25rem 0.5rem',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                }, children: _jsx("span", { role: "img", "aria-label": "delete", children: "\uD83D\uDDD1\uFE0F" }) })] }, page))) })) })), message && (_jsx("div", { style: {
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    background: message.startsWith('✓') ? '#d4edda' : '#f8d7da',
                    color: message.startsWith('✓') ? '#155724' : '#721c24',
                    fontSize: '0.875rem'
                }, children: message }))] }));
}
